import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Lock, Sparkles, ShoppingBag } from "lucide-react";

/**
 * ExclusiveTab — Social „Exklusiv".
 * P6 (Bereichstrennung §3.3): Social kennt KEINE Abos mehr, nur „Öffentlich" +
 * „Einzelkauf". Dieser Tab zeigt daher ALLES, was der User im Social-Bereich
 * gekauft hat (Einzelkäufe / exklusive Beiträge). Keine Abo-Only-Meldung mehr.
 */
export default function ExclusiveTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const results = await Promise.allSettled([
          api.get("/me/purchases/social"),
          api.get("/home/exclusive"),
        ]);
        if (!alive) return;
        if (results[0].status === "fulfilled") setPurchases(results[0].value.data?.purchases || []);
        if (results[1].status === "fulfilled") setRecommendations(results[1].value.data?.recommendations || []);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user === false) {
    return (
      <div data-testid="exclusive-anonymous" className="px-4 py-12 text-center">
        <Lock size={32} className="mx-auto text-[#CCC]" />
        <p className="mt-4 text-sm font-semibold text-[#0A0A0A]">{t("exclusive.title", { defaultValue: "Exklusive Inhalte" })}</p>
        <p className="mt-1 text-xs text-[#999]">{t("exclusive_tab.login_hint")}</p>
        <button
          data-testid="exclusive-login-cta"
          onClick={() => navigate("/login")}
          className="mt-4 rounded-full h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium">
          {t("exclusive.login_cta", { defaultValue: "Einloggen" })}
        </button>
      </div>
    );
  }

  const RecommendationGrid = () => (
    recommendations && recommendations.length > 0 ? (
      <div className="px-4 py-4">
        <p className="text-xs font-medium text-[#666] uppercase tracking-wider mb-3">{t("exclusive.recommendations")}</p>
        <div className="grid grid-cols-2 gap-3">
          {recommendations.map((r) => (
            <button
              key={r.listing_id}
              data-testid={`exclusive-recommendation-${r.listing_id}`}
              onClick={() => navigate(r.area_scope === "STILLTOUCHEDX" ? `/stilltouchedx/${r.listing_id}` : `/marketplace/${r.listing_id}`)}
              className="text-left bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors rounded-lg overflow-hidden">
              {r.media_urls && r.media_urls[0] ? (
                <img src={r.media_urls[0]} alt="" className="w-full aspect-square object-cover" />
              ) : (
                <div className="w-full aspect-square bg-[#F5F5F5] flex items-center justify-center">
                  <ShoppingBag size={20} className="text-[#CCC]" />
                </div>
              )}
              <div className="p-2">
                <p className="text-xs text-[#0A0A0A] truncate">{r.title || t("exclusive.item")}</p>
                {r.price && <p className="text-xs font-medium text-[#0A0A0A] mt-0.5">{Number(r.price).toFixed(2)} €</p>}
              </div>
            </button>
          ))}
        </div>
      </div>
    ) : null
  );

  // Keine Käufe → neutraler, kauf-fokussierter Leerzustand (keine Abo-Meldung) + Vorschläge
  if (purchases.length === 0) {
    return (
      <div data-testid="exclusive-no-purchases" className="pb-8">
        <div className="px-4 py-8 text-center border-b border-[#E5E5E5]">
          <Sparkles size={28} className="mx-auto text-[#CCC]" />
          <p className="mt-3 text-sm font-semibold text-[#0A0A0A]">{t("exclusive.no_purchases_title", { defaultValue: "Noch keine exklusiven Käufe" })}</p>
          <p className="mt-1 text-xs text-[#999] max-w-xs mx-auto">
            {t("exclusive.no_purchases_body", { defaultValue: "Beiträge und Inhalte, die du einzeln kaufst, erscheinen hier – jederzeit abrufbar." })}
          </p>
        </div>
        <RecommendationGrid />
      </div>
    );
  }

  // Gekaufte exklusive Inhalte
  return (
    <div data-testid="exclusive-with-purchases" className="pb-8">
      <div className="px-4 py-3 border-b border-[#E5E5E5]">
        <p className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("exclusive.purchases_title", { defaultValue: "Deine gekauften Inhalte" })}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 px-4 py-4" data-testid="exclusive-purchases-grid">
        {purchases.map((p) => {
          const a = p.asset || {};
          return (
            <button
              key={p.purchase_id || p.asset_id}
              data-testid={`exclusive-purchase-${p.purchase_id || p.asset_id}`}
              onClick={() => p.purchase_id && navigate(`/profile/purchases/social/${p.purchase_id}`)}
              className="text-left bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors rounded-lg overflow-hidden">
              {a.thumbnail_url || a.preview_url ? (
                <img src={a.thumbnail_url || a.preview_url} alt="" className="w-full aspect-square object-cover" />
              ) : (
                <div className="w-full aspect-square bg-[#F5F5F5] flex items-center justify-center">
                  <Lock size={20} className="text-[#CCC]" />
                </div>
              )}
              <div className="p-2">
                <p className="text-xs text-[#0A0A0A] truncate">{a.title || t("exclusive.item")}</p>
                {p.creator?.display_name && <p className="text-[10px] text-[#999] truncate mt-0.5">{p.creator.display_name}</p>}
              </div>
            </button>
          );
        })}
      </div>
      <RecommendationGrid />
    </div>
  );
}
